import React, { useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
} from "@mui/material";
import dayjs from "dayjs";
import { sectionOrder } from "./mapper";

// const sectionOrder = [
//     "Safe Manhours",
//     "Training",
//     "Safety Meeting",
//     "Reward & Recognition",
//     "Monitoring & Measurement",
//     "Finding Tracker",
//     "Environment Reporting",
//     "Safety Resources",
// ];

const transformHseData = (data = []) => {
    const monthFormat = "MMM YYYY";
    const monthsSet = new Set();
    const grouped = {};

    data.forEach((item) => {
        const month = dayjs(item.date).format(monthFormat);
        monthsSet.add(month);

        if (!grouped[item.section]) {
            grouped[item.section] = {};
        }

        if (!grouped[item.section][item.description]) {
            grouped[item.section][item.description] = {};
        }

        grouped[item.section][item.description][month] =
            (grouped[item.section][item.description][month] || 0) + item.value;
    });

    const months = Array.from(monthsSet).sort(
        (a, b) => dayjs(a, monthFormat) - dayjs(b, monthFormat)
    );

    return { grouped, months };
};

const getSectionColor = (section) => {
    switch (section) {
        case "Training":
            return "#f5d7c6";
        case "Safety Meeting":
            return "#c8e6c9";
        case "Reward & Recognition":
            return "#b3d1dc";
        case "Monitoring & Measurement":
            return "#e1bee7";
        default:
            return "#eeeeee";
    }
};

const headerStyle = {
    fontWeight: "bold",
    backgroundColor: "#F9FAFB",
    border: "1px solid #d0d0d0",
    textAlign: "center",
};

const cellStyle = {
    border: "1px solid #d0d0d0",
    minWidth: 300,
};

const cellStyleMonth = {
    border: "1px solid #d0d0d0",
    minWidth: 100,
};

const HSEDataTable = ({ apiData = [], isFetching }) => {
    const { grouped, months } = useMemo(
        () => transformHseData(apiData),
        [apiData]
    );

    console.log("hse normal", apiData, grouped, months)

    const sectionWidth = 150;
    const descriptionWidth = 300;
    const monthWidth = 90;
    const addWidth = 90;

    if (isFetching) {
        return (
            <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const hasData = sectionOrder.filter((section) => grouped[section]).length > 0;

    return (
        <TableContainer component={Paper} sx={{ maxHeight: 650, overflowX: "auto" }}>
            <Table size="small" stickyHeader sx={{
                minWidth: sectionWidth + descriptionWidth + months.length * monthWidth + addWidth,
                borderCollapse: "separate",
                borderSpacing: 0,
            }}>

                <TableHead sx={{
                    "& .MuiTableCell-root": {
                        py: 2,
                    },
                }}>
                    <TableRow>
                        <TableCell align="center" sx={{
                            width: sectionWidth,
                            minWidth: sectionWidth,
                            maxWidth: sectionWidth,
                            position: "sticky",
                            left: 0,
                            zIndex: 4,
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #e0e0e0",
                            fontWeight: "bold",
                        }}>Section Name</TableCell>

                        <TableCell align="center" sx={{
                            width: descriptionWidth,
                            minWidth: descriptionWidth,
                            maxWidth: descriptionWidth,
                            position: "sticky",
                            left: sectionWidth + 1,
                            zIndex: 3,
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #e0e0e0",
                            fontWeight: "bold",
                        }}>Description</TableCell>

                        {months.map((month) => (
                            <TableCell key={month} align="center" sx={{
                                width: monthWidth,
                                minWidth: monthWidth,
                                maxWidth: monthWidth,
                                border: "1px solid #e0e0e0",
                                backgroundColor: "#F9FAFB",
                                fontWeight: "bold",
                            }}>
                                {month}
                            </TableCell>
                        ))}

                        <TableCell
                            align="center"
                            sx={{
                                width: addWidth,
                                minWidth: addWidth,
                                maxWidth: addWidth,
                                border: "1px solid #e0e0e0",
                                backgroundColor: "#F9FAFB",
                                fontWeight: "bold",
                            }}
                        >
                            Total
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>

                    {!hasData ? (
                        <TableRow>
                            <TableCell
                                colSpan={months.length + 3}
                                align="center"
                                sx={{
                                    py: 5,
                                    border: "1px solid #e0e0e0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        color: "#6B7280",
                                    }}
                                >
                                    No Records Found
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (

                        sectionOrder
                            .filter((section) => grouped[section])
                            .map((section) => {
                                const descriptions = grouped[section];
                                const descEntries = Object.entries(descriptions);
                                const rowSpanCount = descEntries.length;

                                return descEntries.map(([description, values], index) => {
                                    const totalValue = months.reduce(
                                        (sum, month) => sum + (values[month] || 0),
                                        0
                                    );
                                    const roundedTotalValue = totalValue.toFixed(2);

                                    return (
                                        <TableRow key={section + description}>

                                            {index === 0 && (
                                                <TableCell
                                                    rowSpan={rowSpanCount}
                                                    sx={{
                                                        width: sectionWidth,
                                                        minWidth: sectionWidth,
                                                        maxWidth: sectionWidth,
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 2,
                                                        backgroundColor: "#F9FAFB",
                                                        border: "1px solid #e0e0e0",
                                                        fontWeight: "bold",
                                                        verticalAlign: "middle",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {section}
                                                </TableCell>
                                            )}

                                            <TableCell sx={{
                                                width: descriptionWidth,
                                                minWidth: descriptionWidth,
                                                maxWidth: descriptionWidth,
                                                position: "sticky",
                                                left: sectionWidth + 1,
                                                zIndex: 1,
                                                backgroundColor: "#fff",
                                                border: "1px solid #e0e0e0",
                                            }}>
                                                {description}
                                            </TableCell>

                                            {months.map((month) => (
                                                <TableCell key={month} align="center" sx={{
                                                    width: monthWidth,
                                                    minWidth: monthWidth,
                                                    maxWidth: monthWidth,
                                                    border: "1px solid #e0e0e0",
                                                }}>
                                                    {values[month] || 0}
                                                </TableCell>
                                            ))}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    width: addWidth,
                                                    minWidth: addWidth,
                                                    maxWidth: addWidth,
                                                    border: "1px solid #e0e0e0",
                                                    fontWeight: "bold",
                                                    backgroundColor: "#F9FAFB",
                                                }}
                                            >
                                                {totalValue ? roundedTotalValue : 0}
                                            </TableCell>
                                        </TableRow>
                                    );
                                });
                            })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default HSEDataTable;